import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// In-chat `review_matter` confirmation card (fn-69.4, R6) — the
// chat-mode counterpart of the headless ArchIE restricted-form card.
//
// `review_matter` is a pure-confirmation CUSTOM tool: the agent has
// assembled a matter draft and pauses (`custom_tool_result_immediate`
// gate) for the user to confirm before any `create_*` runs. Because it
// is an IMMEDIATE ack, the CP requires `form_data` on approve — and the
// confirmed matter IS that `form_data` (memory
// `action-buttons-must-mirror-the-apis`: a bare approval card whose
// Approve carries no `form_data` would 400). So this card, unlike the
// generic `ApprovalActionCard`, posts the matter draft as `form_data`
// on confirm. A decline carries NO `form_data` (the CP accepts a deny
// without one).
//
// Rendering: the assembled matter (the gate's `tool_args`) is shown as
// a plain-text field list — never raw HTML (fn-47 security rule) — with
// the full payload behind a "Show details" disclosure. The card is
// PESSIMISTIC (real side effects): a sending state until the adapter
// resolves, then a quiet decided note; the stream's ack event clears
// the gate (and this card with it).
//
// `readOnly` (R7): informational only — heading, body, the field list,
// and the disclosure render; Confirm/Decline do not exist and
// `decideApproval` is never called.
import { useEffect, useId, useRef, useState } from 'react';
import { DecisionError } from '../../adapter.js';
import { usePlatformAdapter } from '../context.js';
import { CANCEL_LABEL, CONFIRM_DECLINE_LABEL, CONFIRM_MATTER_LABEL, DECISION_SENDING, DECLINE_LABEL, DECLINE_REASON_LABEL, ERROR_COPY, HIDE_DETAILS, REVIEW_MATTER_BODY, REVIEW_MATTER_EMPTY, SHOW_DETAILS, decisionNote, reviewMatterHeading, } from '../copy/copy.js';
/** Canonical bare name of the review-matter confirmation tool. */
export const REVIEW_MATTER_TOOL_NAME = 'review_matter';
/**
 * Canonical bare tool name: the segment after the LAST `___`
 * (federated MCP tools arrive as `<server>___<tool>`). `review_matter`
 * is a bare custom tool today, but normalizing keeps the match robust
 * if it is ever surfaced federated (R4 canonical-normalizer rule).
 */
function bareToolName(name) {
    const sep = name.lastIndexOf('___');
    return sep >= 0 ? name.slice(sep + 3) : name;
}
/** True when an approval row's tool is the review-matter confirmation. */
export function isReviewMatterTool(toolName) {
    return bareToolName(toolName) === REVIEW_MATTER_TOOL_NAME;
}
/** Humanize a draft key (`matter_type` / `matterType` → "Matter type"). */
function humanizeKey(key) {
    const words = key
        .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
        .split(/[\s_.\-]+/)
        .filter((w) => w.length > 0);
    if (words.length === 0)
        return key;
    const joined = words.map((w) => w.toLowerCase()).join(' ');
    return joined.charAt(0).toUpperCase() + joined.slice(1);
}
/** Render a single draft value as plain text (objects/arrays → compact JSON). */
function renderValue(value) {
    if (value === null || value === undefined)
        return '';
    if (typeof value === 'string')
        return value;
    if (typeof value === 'number' || typeof value === 'boolean')
        return String(value);
    try {
        return JSON.stringify(value);
    }
    catch {
        return '';
    }
}
/**
 * Flatten the assembled matter draft into a displayable field list.
 * Empty-string / null / empty-collection values are dropped so the
 * card shows only meaningful fields. Pure + defensive against a
 * non-object payload (returns an empty list → the empty note renders).
 */
export function matterFields(args) {
    const out = [];
    for (const [key, raw] of Object.entries(args)) {
        if (raw === null || raw === undefined)
            continue;
        if (Array.isArray(raw) && raw.length === 0)
            continue;
        if (typeof raw === 'object' && !Array.isArray(raw) && Object.keys(raw).length === 0)
            continue;
        const value = renderValue(raw);
        if (value.length === 0)
            continue;
        out.push({ key, label: humanizeKey(key), value });
    }
    return out;
}
export function ReviewMatterCard(props) {
    const adapter = usePlatformAdapter();
    const headingId = useId();
    const bodyId = useId();
    const containerRef = useRef(null);
    const [phase, setPhase] = useState({ kind: 'idle' });
    const [failed, setFailed] = useState(false);
    const [conflict, setConflict] = useState(false);
    const [detailsOpen, setDetailsOpen] = useState(false);
    // Uncontrolled on purpose (package precedent: ApprovalActionCard's
    // deny form) — no per-keystroke controlled-input plumbing.
    const declineNoteRef = useRef(null);
    const { approval, readOnly, matter } = props;
    const actionable = !readOnly && approval !== null;
    const fields = matterFields(matter);
    // Alertdialog initial focus: the container, NEVER Confirm. Skip in
    // readOnly (an informational card must not steal focus).
    useEffect(() => {
        if (!readOnly)
            containerRef.current?.focus();
    }, [readOnly]);
    const decide = async (decision, note) => {
        if (approval === null)
            return;
        setPhase({ kind: 'working', decision });
        setFailed(false);
        setConflict(false);
        try {
            await adapter.decideApproval({
                sessionId: approval.session_id,
                approvalId: approval.id,
                decision,
                // The confirmed matter IS the immediate-ack `form_data` on
                // approve; a decline carries none (the CP accepts deny without).
                ...(decision === 'approved' ? { formData: { ...matter } } : {}),
                ...(note !== undefined && note.trim().length > 0 ? { denyMessage: note.trim() } : {}),
            });
            // Pessimistic success: the decided note holds until the stream
            // ack clears the gate (the parent unmounts this card).
            setPhase({ kind: 'decided', decision });
            props.onDecided?.(decision);
        }
        catch (err) {
            const kind = err instanceof DecisionError ? err.kind : 'other';
            if (kind === 'conflict') {
                setPhase({ kind: 'conflicted' });
                setConflict(true);
                props.onRequestRefresh?.();
            }
            else {
                setPhase({ kind: 'idle' });
                setFailed(true);
            }
        }
    };
    const working = phase.kind === 'working';
    return (_jsxs("div", { ref: containerRef, className: "sbui-action-card sbui-review-matter-card", "data-testid": "sbui-review-matter-card", ...(readOnly
            ? {}
            : {
                role: 'alertdialog',
                'aria-labelledby': headingId,
                'aria-describedby': bodyId,
                tabIndex: -1,
            }), children: [_jsx("h3", { id: headingId, className: "sbui-action-card-heading", children: reviewMatterHeading(props.agentName) }), _jsx("p", { id: bodyId, className: "sbui-action-card-body", children: REVIEW_MATTER_BODY }), fields.length > 0 ? (_jsx("dl", { className: "sbui-review-matter-fields", "data-testid": "sbui-review-matter-fields", children: fields.map((f) => (_jsxs("div", { className: "sbui-review-matter-field", children: [_jsx("dt", { className: "sbui-review-matter-field-label", children: f.label }), _jsx("dd", { className: "sbui-review-matter-field-value", children: f.value })] }, f.key))) })) : (_jsx("p", { className: "sbui-review-matter-empty", children: REVIEW_MATTER_EMPTY })), _jsxs("button", { type: "button", className: "sbui-action-card-details-toggle", "aria-expanded": detailsOpen, onClick: () => setDetailsOpen((v) => !v), children: [_jsx("span", { className: "sbui-chat-activity-chevron", "aria-hidden": "true" }), detailsOpen ? HIDE_DETAILS : SHOW_DETAILS] }), detailsOpen && (_jsx("pre", { className: "sbui-action-card-details", children: JSON.stringify(matter, null, 2) })), actionable &&
                (phase.kind === 'conflicted' ? null : phase.kind === 'decided' ? (_jsx("output", { className: "sbui-action-card-decided", "data-testid": "sbui-review-matter-decided", children: decisionNote(phase.decision === 'approved' ? 'allow' : 'deny') })) : phase.kind === 'declining' ||
                    (phase.kind === 'working' && phase.decision === 'rejected') ? (_jsxs("div", { className: "sbui-action-card-decline-form", children: [_jsxs("label", { className: "sbui-action-card-decline-label", children: [DECLINE_REASON_LABEL, _jsx("textarea", { ref: declineNoteRef, className: "sbui-action-card-decline-note", rows: 2, disabled: working })] }), _jsxs("div", { className: "sbui-action-card-actions", children: [_jsx("button", { type: "button", className: "sbui-action-card-confirm-decline", disabled: working, onClick: () => void decide('rejected', declineNoteRef.current?.value ?? ''), children: working ? DECISION_SENDING : CONFIRM_DECLINE_LABEL }), _jsx("button", { type: "button", className: "sbui-action-card-cancel", disabled: working, onClick: () => setPhase({ kind: 'idle' }), children: CANCEL_LABEL })] })] })) : (_jsxs("div", { className: "sbui-action-card-actions", children: [_jsx("button", { type: "button", className: "sbui-action-card-approve", disabled: working, onClick: () => void decide('approved'), children: working ? DECISION_SENDING : CONFIRM_MATTER_LABEL }), _jsx("button", { type: "button", className: "sbui-action-card-decline", disabled: working, onClick: () => setPhase({ kind: 'declining' }), children: DECLINE_LABEL })] }))), conflict && (_jsx("output", { className: "sbui-action-card-conflict", "data-testid": "sbui-action-card-conflict", children: ERROR_COPY.conflict })), failed && (_jsx("p", { className: "sbui-action-card-error", role: "alert", children: ERROR_COPY.decision }))] }));
}
//# sourceMappingURL=review-matter-card.js.map